import classNames from 'classnames';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import AdvertCardAddressRow from '@Components/Advert/AdvertCardAddressRow';
import AdvertCardPriceRow from '@Components/Advert/AdvertCardPriceRow';
import AdvertCardTitle from '@Components/Advert/AdvertCardTitle';
import AdvertPicture from '@Components/Advert/AdvertPicture';
import { ButtonSizes, ButtonStyles } from '@Components/Buttons/Button';
import ButtonLink from '@Components/Buttons/ButtonLink';
import Row from '@Components/layout/Row';
import Typography from '@Components/Typography';
import { Messages } from '@Config/messages';
import { RouteConfig } from '@Config/routes';
import { AdvertFragment, AdvertStatus } from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import { getUserIsLogged } from '@Store/auth/auth.selector';

import AdFavoriteButton from '../AdFavoriteButton';

import styles from './AdvertCard.module.scss';

export interface AdvertCardProps {
  advert: AdvertFragment;
}

const AdvertCard: React.FunctionComponent<AdvertCardProps> = ({ advert }) => {
  const { title, advertType, serviceType, status, slug, mainImageThumbnail, validUntil } = advert;

  const t = useTranslations();
  const isLoggedIn = useSelector(getUserIsLogged);
  const advertLink = RouteConfig.Advert.buildLink({ slug });

  const isAdExpired = validUntil && new Date(validUntil) < new Date();

  return (
    <div
      className={classNames(styles.container, {
        [styles.privateStatus]: status === AdvertStatus.Private,
      })}
    >
      <Link to={advertLink}>
        <AdvertPicture url={mainImageThumbnail} advertType={advertType} serviceType={serviceType} alt={title} />
      </Link>

      {isLoggedIn && !isAdExpired && (
        <AdFavoriteButton
          favoriteAdvertId={advert.id}
          isAdFavorited={advert.isFavorited}
          size={ButtonSizes.unset}
          showSuccessMessage
          inPicture
        />
      )}

      <div className={styles.contentContainer}>
        <div className={styles.content}>
          <div className={styles.wrapper}>
            <AdvertCardTitle advert={advert} />
            <AdvertCardAddressRow advert={advert} />
            <Row>
              {isAdExpired ? (
                <Typography tag="div" msg={t(Messages.adExpired)} size="m" className={styles.expired} />
              ) : (
                <Link to={advertLink}>
                  <AdvertCardPriceRow advert={advert} />
                </Link>
              )}
            </Row>
          </div>
        </div>

        <div className={styles.viewButtonContainer}>
          <ButtonLink link={advertLink} style={ButtonStyles.plainBold} size={ButtonSizes.unset}>
            <Typography tag="span" msg={t('msg_button_view_add')} />
          </ButtonLink>
        </div>
      </div>
    </div>
  );
};

export default AdvertCard;
